import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RequiresSubscription } from "@/components/RequiresSubscription";
import { CookieNotice } from "@/components/CookieNotice";
import { initGA4 } from "@/components/SEOHead";

// Pages
import LandingPage from "@/pages/LandingPage";
import NewOnboardingPage from "@/pages/NewOnboardingPage";
import DashboardPage from "@/pages/DashboardPage";
import CountryDetailPage from "@/pages/CountryDetailPage";
import SectorsPage from "@/pages/SectorsPage";
import SectorDetailPage from "@/pages/SectorDetailPage";
import ReportsPage from "@/pages/ReportsPage";
import ReportDetailPage from "@/pages/ReportDetailPage";
import AlertsPage from "@/pages/AlertsPage";
import SettingsPage from "@/pages/SettingsPage";
import BriefingDetailPage from "@/pages/BriefingDetailPage";
import HistoryPage from "@/pages/HistoryPage";
import SuppliersPage from "@/pages/SuppliersPage";
import SupplierDetailPage from "@/pages/SupplierDetailPage";
import CountriesPage from "@/pages/CountriesPage";
import ScenarioPage from "@/pages/ScenarioPage";
import ConcentrationPage from "@/pages/ConcentrationPage";
import AukusCompliancePage from "@/pages/AukusCompliancePage";
import ModernSlaveryPage from "@/pages/ModernSlaveryPage";
import ComparativeSourcingPage from "@/pages/ComparativeSourcingPage";
import SignalsPage from "@/pages/SignalsPage";
import WatchlistPage from "@/pages/WatchlistPage";
import SourcingPage from "@/pages/SourcingPage";
import PortsRoutesPage from "@/pages/PortsRoutesPage";
import PricingPage from "@/pages/PricingPage";
import BillingPage from "@/pages/BillingPage";
import BillingSuccessPage from "@/pages/BillingSuccessPage";
import BillingCancelledPage from "@/pages/BillingCancelledPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import InsightsPage from "@/pages/InsightsPage";
import InsightArticlePage from "@/pages/InsightArticlePage";
import CountryPublicPage from "@/pages/CountryPublicPage";
import AboutPage from "@/pages/AboutPage";
import TrustPage from "@/pages/TrustPage";

// Auth pages
import SignInPage from "@/pages/SignInPage";
import RegisterPage from "@/pages/RegisterPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import MfaSetupPage from "@/pages/MfaSetupPage";

// Admin pages
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminOrganisationsPage from "@/pages/admin/AdminOrganisationsPage";
import AdminOrgDetailPage from "@/pages/admin/AdminOrgDetailPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminUserDetailPage from "@/pages/admin/AdminUserDetailPage";
import AdminAuditLogPage from "@/pages/admin/AdminAuditLogPage";
import AdminSettingsPage from "@/pages/admin/AdminSettingsPage";
import AdminLeadsPage from "@/pages/admin/AdminLeadsPage";
import AdminBlogPage from "@/pages/admin/AdminBlogPage";
import AdminMonitoringPage from "@/pages/admin/AdminMonitoringPage";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { initErrorReporter } from "@/lib/errorReporter";

initErrorReporter();

/* Helper: auth + subscription gate for product routes */
function Gated({ children }) {
  return <ProtectedRoute><RequiresSubscription>{children}</RequiresSubscription></ProtectedRoute>;
}

/* Country pages: public SEO page for visitors, full product detail page for signed-in users */
function CountryRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Gated><CountryDetailPage /></Gated> : <CountryPublicPage />;
}

function App() {
  useEffect(() => { initGA4(); }, []);

  return (
    <div className="App">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <ErrorBoundary>
      <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/signin" element={<SignInPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/insights/:slug" element={<InsightArticlePage />} />
            <Route path="/trust" element={<TrustPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/countries/:countryCode" element={<CountryRoute />} />            {/* Auth flow (no subscription check) */}
            <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
            <Route path="/mfa-setup" element={<ProtectedRoute><MfaSetupPage /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute><NewOnboardingPage /></ProtectedRoute>} />
            <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
            <Route path="/billing/success" element={<ProtectedRoute><BillingSuccessPage /></ProtectedRoute>} />
            <Route path="/billing/cancelled" element={<ProtectedRoute><BillingCancelledPage /></ProtectedRoute>} />

            {/* Gated product routes (auth + subscription required) */}
            <Route path="/dashboard" element={<Gated><DashboardPage /></Gated>} />
            <Route path="/countries" element={<Gated><CountriesPage /></Gated>} />
            <Route path="/sectors" element={<Gated><SectorsPage /></Gated>} />
            <Route path="/sectors/:sectorName" element={<Gated><SectorDetailPage /></Gated>} />
            <Route path="/reports" element={<Gated><ReportsPage /></Gated>} />
            <Route path="/reports/:reportId" element={<Gated><ReportDetailPage /></Gated>} />
            <Route path="/alerts" element={<Gated><AlertsPage /></Gated>} />
            <Route path="/settings" element={<Gated><SettingsPage /></Gated>} />
            <Route path="/briefings/:briefingId" element={<Gated><BriefingDetailPage /></Gated>} />
            <Route path="/history" element={<Gated><HistoryPage /></Gated>} />
            <Route path="/suppliers" element={<Gated><SuppliersPage /></Gated>} />
            <Route path="/suppliers/:supplierId" element={<Gated><SupplierDetailPage /></Gated>} />
            <Route path="/scenarios" element={<Gated><ScenarioPage /></Gated>} />
            <Route path="/concentration" element={<Gated><ConcentrationPage /></Gated>} />
            <Route path="/aukus" element={<Gated><AukusCompliancePage /></Gated>} />
            <Route path="/modern-slavery" element={<Gated><ModernSlaveryPage /></Gated>} />
            <Route path="/sourcing" element={<Gated><ComparativeSourcingPage /></Gated>} />
            <Route path="/signals" element={<Gated><SignalsPage /></Gated>} />
            <Route path="/watchlist" element={<Gated><WatchlistPage /></Gated>} />
            <Route path="/sourcing-alternatives" element={<Gated><SourcingPage /></Gated>} />
            <Route path="/ports-routes" element={<Gated><PortsRoutesPage /></Gated>} />

            {/* Admin routes (auth + admin only, no subscription check) */}
            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboardPage /></ProtectedRoute>} />
            <Route path="/admin/organisations" element={<ProtectedRoute adminOnly><AdminOrganisationsPage /></ProtectedRoute>} />
            <Route path="/admin/organisations/:id" element={<ProtectedRoute adminOnly><AdminOrgDetailPage /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminUsersPage /></ProtectedRoute>} />
            <Route path="/admin/users/:id" element={<ProtectedRoute adminOnly><AdminUserDetailPage /></ProtectedRoute>} />
            <Route path="/admin/audit-log" element={<ProtectedRoute adminOnly><AdminAuditLogPage /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute adminOnly><AdminSettingsPage /></ProtectedRoute>} />
            <Route path="/admin/monitoring" element={<ProtectedRoute adminOnly><AdminMonitoringPage /></ProtectedRoute>} />
            <Route path="/admin/leads" element={<ProtectedRoute adminOnly><AdminLeadsPage /></ProtectedRoute>} />
            <Route path="/admin/blog" element={<ProtectedRoute adminOnly><AdminBlogPage /></ProtectedRoute>} />

          </Routes>
          <CookieNotice />
        </BrowserRouter>
      </AuthProvider>
      </HelmetProvider>
      </ErrorBoundary>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
